import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    searchOnchangeHandler,
} from "../commonmethod/simmsDrawerMethod";
import {getFlatCode} from "../commonmethod/CommonMethod";
import {childrenKeys} from "../../constant/CodeSpecNum";
import {List} from "../drawer/simms/List";
import SearchSelectorPagination from "./SearchSelectorPagination";
import useCodeData from "../../hooks/useCodeData";
import {useSelector} from "react-redux";
import {isAdmin} from "../commonmethod/CommonMethod";
import {Link} from "react-router-dom";
import {useHandleApiSelect} from "../../api/apiselector/ApiSelector";
import {handleActiveError, handleActiveToast} from "../sonner/ToastBasic";
import {useTranslation} from "react-i18next";
import SearchSelectorPanelList from "./SearchSelectorPanelList";
import SearchSelectorFilterTree from "./SearchSelectorFilterTree";
import {parseSearchSelectorFilters} from "./parseSearchSelectorFilters";
import {
    buildMultiArrayLabel,
    getItemLabel,
    getItemMeta,
    getMultiDataIds,
    resolveControlledMultiFlatNodes,
} from "./searchSelectorUtils";

function SearchSelectorPanel({
    open,
    onClose,
    data,
    onChange,
    recursive,
    label,
    constraint,
    children,
    title,
    entity,
    uniqueKey,
    parentKey,
    showLabel,
    notInsert,
    setLabel,
    placeHolder,
    useMultiSearch,
    controlledMulti,
    matchActiveByNameWhenIdMissing = false,
    showClearAction = false,
}) {
    const [expandedMenus, setExpandedMenus] = useState({});
    const [autoClose] = useState(true);
    const [checkedNodeIds, setCheckedNodeIds] = useState([]);
    const [selectedFilterKey, setSelectedFilterKey] = useState("all");
    const [searchText, setSearchText] = useState("");
    const {dataSet, loading, pageInfo, handlePageChange, handleSearch, searchConditions} = useCodeData(entity, uniqueKey);
    const [subSelected, setSubSelected] = useState({});
    const drawerMultiSearchKey = `drawerMultiSearch:${title}:${entity}:${parentKey ?? ""}:${uniqueKey ?? ""}`;
    const hadDrawerMultiSearchCacheRef = useRef(!!sessionStorage.getItem(drawerMultiSearchKey));

    const dataLabel = Array.isArray(data)
        ? (data[0]?.name ?? data[0]?.assetName ?? data[0]?.title)
        : (data?.name ?? data?.assetName ?? data?.title);
    const [singleModeLabel, setSingleModeLabel] = useState(dataLabel ?? placeHolder ?? "선택");

    let codes = [];
    if (!loading && dataSet) {
        const content = dataSet.content ?? dataSet;
        if (parentKey) {
            codes = content.filter((each) => each.id === parentKey);
        } else {
            codes = [{children: content}];
        }
    }

    const collectNodesFromNode = (node) => {
        if (!node) return [];
        let nodes = [node];
        if (Array.isArray(node.children) && node.children.length > 0) {
            node.children.forEach((child) => {
                nodes = nodes.concat(collectNodesFromNode(child));
            });
        }
        return nodes;
    };

    const findNodeById = (list, id) => {
        for (const node of (list ?? [])) {
            if (node.id === id) return node;
            const found = findNodeById(node.children, id);
            if (found) return found;
        }
        return null;
    };

    const resolveMultiSearchFlatNodes = (selectedIds) => {
        const tree = codes?.[0]?.children ?? [];
        const allNodes = selectedIds.flatMap((id) => {
            const node = findNodeById(tree, id);
            return collectNodesFromNode(node);
        });
        const seen = new Set();
        return allNodes.filter((node) => {
            if (!node || seen.has(node.id)) return false;
            seen.add(node.id);
            return true;
        });
    };

    const getNodeLabel = (node) => node?.assetName ?? node?.name ?? node?.title ?? "";
    const baseLabel = useMultiSearch
        ? (singleModeLabel ?? dataLabel ?? placeHolder ?? "선택")
        : (dataLabel ?? placeHolder ?? "선택");
    const selectedFlatNodes = resolveMultiSearchFlatNodes(checkedNodeIds);
    const multiDisplayLabel = controlledMulti && Array.isArray(data)
        ? buildMultiArrayLabel(data, placeHolder)
        : selectedFlatNodes.length === 0
            ? baseLabel
            : selectedFlatNodes.length === 1
                ? getNodeLabel(selectedFlatNodes[0])
                : `${getNodeLabel(selectedFlatNodes[0])} 외 ${selectedFlatNodes.length - 1}개`;
    const displayLabel = useMultiSearch ? multiDisplayLabel : baseLabel;

    const handleOnChange = (...args) => {
        if (useMultiSearch) return;
        onChange(...args);
        if (autoClose) {
            onClose();
        }
    };

    const handleMultiCheckChange = (item, checked) => {
        setCheckedNodeIds((prev) => {
            const next = checked ? [...new Set([...prev, item.id])] : prev.filter((id) => id !== item.id);
            const listNodes = resolveMultiSearchFlatNodes(next);
            const flatNodes = controlledMulti && Array.isArray(data)
                ? resolveControlledMultiFlatNodes(next, listNodes, data)
                : listNodes;
            onChange({__multiFlat: flatNodes});
            return next;
        });
    };

    const effectiveCheckedIds = selectedFlatNodes.map((node) => node.id);

    useEffect(() => {
        if (!useMultiSearch) {
            setSingleModeLabel(dataLabel ?? placeHolder ?? "선택");
        }
    }, [useMultiSearch, dataLabel, placeHolder]);

    useEffect(() => {
        if (!open) return;

        if (controlledMulti && Array.isArray(data)) {
            setCheckedNodeIds(getMultiDataIds(data));
            return;
        }

        /** 단일 선택 모드에서는 이전 다중선택 sessionStorage 잔재가 active로 표시되지 않도록 복원 skip */
        if (!useMultiSearch) {
            setCheckedNodeIds([]);
            return;
        }

        const saved = sessionStorage.getItem(drawerMultiSearchKey);
        if (!saved) return;

        try {
            const parsed = JSON.parse(saved);
            const savedCheckedNodeIds = Array.isArray(parsed?.checkedNodeIds) ? parsed.checkedNodeIds : [];
            const enabled = !!parsed?.enabled && savedCheckedNodeIds.length > 0;

            setCheckedNodeIds(enabled ? savedCheckedNodeIds : []);

            if (enabled && typeof setLabel === "function" && typeof parsed?.displayLabel === "string") {
                setLabel(parsed.displayLabel);
            }
        } catch (e) {
            setCheckedNodeIds([]);
        }
    }, [open, drawerMultiSearchKey, setLabel, controlledMulti, data, useMultiSearch]);

    useEffect(() => {
        if (!useMultiSearch || !controlledMulti || !Array.isArray(data)) return;
        setCheckedNodeIds(getMultiDataIds(data));
    }, [controlledMulti, data, useMultiSearch]);

    useEffect(() => {
        if (!useMultiSearch || controlledMulti) return;

        const saved = sessionStorage.getItem(drawerMultiSearchKey);
        const hasActiveMultiSelection = checkedNodeIds.length > 0;

        if (!saved && hadDrawerMultiSearchCacheRef.current && hasActiveMultiSelection) {
            setCheckedNodeIds([]);
            onChange({__multiFlat: []});
            hadDrawerMultiSearchCacheRef.current = false;
            return;
        }

        if (!hasActiveMultiSelection) {
            sessionStorage.removeItem(drawerMultiSearchKey);
            hadDrawerMultiSearchCacheRef.current = false;
            return;
        }

        sessionStorage.setItem(drawerMultiSearchKey, JSON.stringify({
            enabled: true,
            checkedNodeIds,
            displayLabel,
        }));
        hadDrawerMultiSearchCacheRef.current = true;
    }, [drawerMultiSearchKey, useMultiSearch, checkedNodeIds, displayLabel, onChange, controlledMulti]);

    useEffect(() => {
        if (typeof setLabel === "function") {
            setLabel(displayLabel);
        }
    }, [displayLabel, setLabel]);

    const onChangeHandler = useCallback((e, keepOpened = false) => {
        if (!recursive) return;
        const value = e.target ? e : {target: {value: e}};
        const resetOthers = !keepOpened;
        searchOnchangeHandler(dataSet, value, expandedMenus, setExpandedMenus, recursive, resetOthers);
    }, [recursive, dataSet, expandedMenus]);

    useEffect(() => {
        if (open && dataSet && data && recursive) {
            onChangeHandler({
                target: {
                    value: data?.name ?? data?.assetName ?? data?.title ?? "",
                },
            }, true);
        }
    }, [open, dataSet, data, recursive, onChangeHandler]);

    const filterConfigs = useMemo(() => parseSearchSelectorFilters(children), [children]);
    const panelUniqueKey = uniqueKey ?? `${entity}_${title ?? "panel"}`;

    const buildSearchParams = useCallback(() => {
        const params = {...subSelected};
        /**
         * useCodeData.handleSearch 는 빈 객체를 받으면 이전 searchTxt 를 유지하므로,
         * 검색어가 비어도 빈 문자열을 명시적으로 보내 이전 검색 결과가 캐시처럼 남는 것을 막는다.
         */
        params.searchTxt = (searchText ?? "").trim();
        return params;
    }, [subSelected, searchText]);

    useEffect(() => {
        if (!open) return;
        handleSearch(buildSearchParams());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subSelected, searchText, open]);

    const handleSelectAllFilter = useCallback(() => {
        setSelectedFilterKey("all");
        setSubSelected({});
    }, []);

    const handleFilterSelect = useCallback((target, node) => {
        const filterKey = `${target}:${node.id}`;
        if (selectedFilterKey === filterKey) {
            handleSelectAllFilter();
            return;
        }

        setSelectedFilterKey(filterKey);
        setSubSelected(() => {
            const next = {};
            const trimmed = searchText?.trim();
            if (trimmed) {
                next.searchTxt = trimmed;
            }

            filterConfigs.forEach((config) => {
                if (config.target === target) return;
                delete next[config.target];
                const listKey = childrenKeys[config.target];
                if (listKey) delete next[listKey];
            });

            next[target] = node;
            const listKey = childrenKeys[target];
            if (listKey) {
                next[listKey] = getFlatCode([node]);
            }
            return next;
        });
    }, [filterConfigs, handleSelectAllFilter, searchText, selectedFilterKey]);

    const handleSearchInputChange = (e) => {
        setSearchText(e.target.value);
    };

    const baseListItems = codes[0]?.children ?? [];
    /**
     * 백엔드 검색이 name(label)만 매칭하는 경우에도 panel 화면에 보이는 row 의 meta(부서·직급·사업자번호 등)
     * 까지 검색되도록 클라이언트 측 보조 필터를 추가. 백엔드가 이미 meta 까지 검색하면 결과 동일.
     */
    const trimmedSearchText = (searchText ?? "").trim().toLowerCase();
    const listItems = useMemo(() => {
        if (!trimmedSearchText) return baseListItems;
        return baseListItems.filter((item) => {
            const label = getItemLabel(item).toLowerCase();
            if (label.includes(trimmedSearchText)) return true;
            const meta = (getItemMeta(item, entity) ?? "").toLowerCase();
            return meta.includes(trimmedSearchText);
        });
    }, [baseListItems, trimmedSearchText, entity]);

    const currentPageNodes = listItems.flatMap(collectNodesFromNode);
    const currentPageIds = [...new Set(
        currentPageNodes
            .map((item) => item?.id)
            .filter((id) => id != null),
    )];
    const effectiveCheckedIdSet = new Set(effectiveCheckedIds);
    const currentPageCheckedCount = currentPageIds.reduce(
        (count, id) => count + (effectiveCheckedIdSet.has(id) ? 1 : 0),
        0,
    );
    const isCurrentPageChecked =
        currentPageIds.length > 0 && currentPageCheckedCount === currentPageIds.length;
    const isCurrentPageIndeterminate =
        currentPageCheckedCount > 0 && !isCurrentPageChecked;

    const handleCurrentPageCheckChange = (checked) => {
        setCheckedNodeIds((prev) => {
            const currentPageIdSet = new Set(currentPageIds);
            const next = checked
                ? [...new Set([...prev, ...currentPageIds])]
                : prev.filter((id) => !currentPageIdSet.has(id));
            const flatNodes = controlledMulti && Array.isArray(data)
                ? resolveControlledMultiFlatNodes(next, currentPageNodes, data)
                : resolveMultiSearchFlatNodes(next);

            onChange({__multiFlat: flatNodes});
            return next;
        });
    };

    const user = useSelector((state) => state.auth.user);
    const adminAuth = isAdmin(user);
    const [state, setState] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [inputData, setInputData] = useState(null);
    const handleApiSelect = useHandleApiSelect();
    const {t} = useTranslation();

    const handleInputEnter = (e, top = false) => {
        if (e.key === "Enter") {
            handleApiSelect(
                entity,
                top ? (parentKey ? parentKey : 1) : selectedId,
                parentKey,
                top ? inputData?.top : inputData?.sub,
                () => {
                    setInputData((prev) => (top ? {...prev, top: ""} : {...prev, sub: ""}));
                    handleActiveToast({message: t("saved")});
                },
                () => {
                    handleActiveError({message: t("duplicatedNameExisted")});
                },
            );
        }
    };

    if (!open) return null;

    const showFilterTree = filterConfigs.length > 0;
    const hasSingleSelection = !!(data && !Array.isArray(data) && (data.id != null || data.name || data.assetName || data.title));

    return (
        <div
            className={[
                "search-selector-panel",
                "no-drag",
                showFilterTree ? "" : "search-selector-panel--compact",
                showClearAction ? "search-selector-panel--excel-cell" : "",
            ].filter(Boolean).join(" ")}
        >
            <div
                className={[
                    "search-selector-panel-body",
                    showFilterTree ? "search-selector-panel-body--with-sidebar" : "",
                ].filter(Boolean).join(" ")}
            >
                {showFilterTree && (
                    <SearchSelectorFilterTree
                        filters={filterConfigs}
                        panelUniqueKey={panelUniqueKey}
                        selectedFilterKey={selectedFilterKey}
                        onSelectAll={handleSelectAllFilter}
                        onSelectFilter={handleFilterSelect}
                    />
                )}

                <div className="search-selector-panel-main">
                    <div className="search-selector-panel-search-wrap">
                        <input
                            type="text"
                            autoComplete="off"
                            className="search-selector-panel-search"
                            placeholder="검색하세요."
                            value={searchText}
                            onChange={handleSearchInputChange}
                        />
                    </div>

                    {useMultiSearch && (
                        <div className="search-selector-panel-page-select">
                            <label className="search-selector-panel-page-select-label">
                                <input
                                    type="checkbox"
                                    className="search-selector-panel-checkbox"
                                    checked={isCurrentPageChecked}
                                    ref={(element) => {
                                        if (element) {
                                            element.indeterminate = isCurrentPageIndeterminate;
                                        }
                                    }}
                                    disabled={loading || currentPageIds.length === 0}
                                    onChange={(e) => handleCurrentPageCheckChange(e.target.checked)}
                                />
                                <span>현재 페이지 선택</span>
                            </label>
                            <span className="search-selector-panel-page-select-count">
                                {currentPageCheckedCount}/{currentPageIds.length}
                            </span>
                        </div>
                    )}

                    {showClearAction && hasSingleSelection && (
                        <div className="search-selector-panel-clear-wrap" style={{padding: "0 12px 8px"}}>
                            <button
                                type="button"
                                className="search-selector-panel-clear-btn"
                                onClick={() => {
                                    handleOnChange(null);
                                }}
                                style={{
                                    width: "100%",
                                    height: 30,
                                    borderRadius: 8,
                                    border: "1px solid #e5e7eb",
                                    background: "#fff",
                                    fontSize: 12,
                                    color: "#374151",
                                }}
                            >
                                선택 취소
                            </button>
                        </div>
                    )}

                    <div className="search-selector-panel-list">
                        {loading ? (
                            <div className="search-selector-loading">불러오는 중...</div>
                        ) : listItems.length > 0 ? (
                            recursive ? (
                                <List
                                    list={listItems}
                                    onChange={handleOnChange}
                                    data={data}
                                    expandedMenus={expandedMenus}
                                    setExpandedMenus={setExpandedMenus}
                                    required={constraint?.required}
                                    recursive={recursive}
                                    state={{state, setState}}
                                    selectedId={{selectedId, setSelectedId}}
                                    inputData={{inputData, setInputData}}
                                    handleInputEnter={handleInputEnter}
                                    multiSearch={useMultiSearch}
                                    checkedIds={effectiveCheckedIds}
                                    onMultiCheckChange={handleMultiCheckChange}
                                    matchActiveByNameWhenIdMissing={matchActiveByNameWhenIdMissing}
                                />
                            ) : (
                                <SearchSelectorPanelList
                                    list={listItems}
                                    entity={entity}
                                    data={data}
                                    multiSelect={useMultiSearch}
                                    checkedIds={effectiveCheckedIds}
                                    onSelect={handleOnChange}
                                    onMultiCheckChange={handleMultiCheckChange}
                                    matchActiveByNameWhenIdMissing={matchActiveByNameWhenIdMissing}
                                />
                            )
                        ) : (
                            <div className="search-selector-empty">데이터 없음</div>
                        )}
                    </div>

                    <SearchSelectorPagination page={pageInfo} onPageChange={handlePageChange} />
                </div>

                {showFilterTree && (
                    <>
                        <button
                            type="button"
                            className="search-selector-panel-mobile-close"
                            onClick={onClose}
                            aria-label="선택 창 닫기"
                        >
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            <span>닫기</span>
                        </button>
                        <div className="search-selector-panel-mobile-pagination">
                            <SearchSelectorPagination page={pageInfo} onPageChange={handlePageChange} />
                        </div>
                    </>
                )}
            </div>

            {adminAuth && recursive && !showLabel && !notInsert && (
                <div className="search-selector-panel-footer">
                    {!state ? (
                        <Link className="btn_save mt-2" style={{marginLeft: 0, textIndent: 0}} onClick={() => setState(!state)}>등록</Link>
                    ) : (
                        <div className="mt-2 flex items-center justify-between" style={{textIndent: 0}}>
                            <Link className="btn_delete ml-0" style={{marginLeft: 0}} onClick={() => {
                                setState(!state);
                                setSelectedId(null);
                            }}>취소</Link>
                            <span className="text-[11px] text-gray-500">※ 수정 및 삭제는 환경설정에서 가능합니다</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default SearchSelectorPanel;
